import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { mockStudents as initialStudents } from "../../data/mockStudents";
import { getCurrentUser } from "../../auth";
import StudentForm from "./StudentForm";
import { updateStudent, refreshStudentsFromFirestore, getAllStudents, deleteStudent, addStudent, generateAvailableStudentId } from "../../services/studentsService";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";

export default function Students() {
  const [query, setQuery] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [strandFilter, setStrandFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rev, setRev] = useState(0);

  const currentUser = getCurrentUser();
  const userCampus = currentUser?.campus;
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin" || userCampus === "all";

  const [students, setStudents] = useState(() => getAllStudents());
  React.useEffect(() => {
    if (!USE_LOCAL_FALLBACK) {
      refreshStudentsFromFirestore().then(list => {
        setStudents(getAllStudents());
        setRev(r => r + 1);
      });
    } else {
      setStudents(getAllStudents());
    }
  }, []);
  React.useEffect(() => {
    try {
      const gq = localStorage.getItem("globalStudentSearch");
      if (gq) {
        setQuery(gq);
        localStorage.removeItem("globalStudentSearch");
      }
    } catch (e) { /* ignore */ }
  }, []);
  const allStudents = useMemo(() => students, [students, rev]);
  const grades = useMemo(() => Array.from(new Set(allStudents.map(s => s.gradeLevel).filter(Boolean))), [allStudents]);
  const strands = useMemo(() => Array.from(new Set(allStudents.map(s => s.strand).filter(Boolean))), [allStudents]);
  const statuses = useMemo(() => Array.from(new Set(allStudents.map(s => s.enrollmentStatus).filter(Boolean))), [allStudents]);

  const visible = useMemo(() => {
    return allStudents.filter(s => {
      if (!isAdmin && userCampus && s.campus !== userCampus) return false;
      const q = query.trim().toLowerCase();
      if (q && !(String(s.fullname || `${s.firstName} ${s.lastName}`).toLowerCase().includes(q) || String(s.id).toLowerCase().includes(q))) {
        return false;
      }
      if (gradeFilter && s.gradeLevel !== gradeFilter) return false;
      if (strandFilter && s.strand !== strandFilter) return false;
      if (statusFilter && s.enrollmentStatus !== statusFilter) return false;
      return true;
    });
  }, [query, gradeFilter, strandFilter, statusFilter, isAdmin, userCampus, allStudents]);

  function downloadText(filename, text) {
    const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const [editId, setEditId] = useState(null);
  const editStudent = allStudents.find(s => String(s.id) === String(editId));
  const [importPreview, setImportPreview] = useState(null);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Students</h3>
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={async () => {
              try {
                for (const s of allStudents) {
                  const lc = String(s.campus || "").toLowerCase();
                  if (lc !== s.campus) {
                    // eslint-disable-next-line no-await-in-loop
                    await updateStudent(s.id, { campus: lc });
                  }
                }
                setStudents(getAllStudents());
                setRev(r => r + 1);
                alert("Campus normalized to lowercase for all students.");
              } catch (e) {
                alert("Failed to normalize campuses.");
              }
            }}
          >
            Normalize Campus
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              const header = ["id","lrn","firstName","middleName","lastName","fullname","gradeLevel","levelName","paymentMode","enrollmentStatus","strand","section","campus","gender","birthdate","address","contact","parentEmail","guardianEmail","enrollmentYear"].join(",");
              downloadText("students_template.csv", header + "\n");
            }}
          >
            Download Template (CSV)
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              const header = ["id","lrn","firstName","middleName","lastName","fullname","gradeLevel","strand","section","campus","gender","birthdate","address","contact","parentEmail","guardianEmail","paymentMode","enrollmentStatus","enrollmentYear"];
              const rows = allStudents.map(s => [
                s.id || "",
                s.lrn || "",
                s.firstName || "",
                s.middleName || "",
                s.lastName || "",
                s.fullname || "",
                s.gradeLevel || "",
                s.strand || "",
                s.section || "",
                s.campus || "",
                s.gender || "",
                s.birthdate || "",
                s.address || "",
                s.contact || "",
                s.parentEmail || "",
                s.guardianEmail || "",
                s.paymentMode || "",
                s.enrollmentStatus || "",
                s.enrollmentYear || ""
              ].map(v => String(v).replace(/,/g," ")).join(","));
              downloadText("students_export.csv", [header.join(","), ...rows].join("\n"));
            }}
          >
            Export CSV
          </button>
          <input
            type="file"
            className="form-control form-control-sm"
            accept=".csv,application/json"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const text = String(reader.result || "").replace(/^\uFEFF/, "");
                  let rows = [];
                  if (file.name.toLowerCase().endsWith(".json")) {
                    const data = JSON.parse(text);
                    rows = Array.isArray(data) ? data : [];
                  } else {
                    const detectDelimiter = (input) => {
                      let inQuotes = false;
                      let commas = 0;
                      let semis = 0;
                      let tabs = 0;
                      for (let i = 0; i < input.length; i++) {
                        const ch = input[i];
                        const next = input[i + 1];
                        if (inQuotes) {
                          if (ch === "\"" && next === "\"") { i++; continue; }
                          if (ch === "\"") { inQuotes = false; continue; }
                          continue;
                        }
                        if (ch === "\"") { inQuotes = true; continue; }
                        if (ch === "\n") break;
                        if (ch === ",") commas++;
                        else if (ch === ";") semis++;
                        else if (ch === "\t") tabs++;
                      }
                      const best = Math.max(commas, semis, tabs);
                      if (best === 0) return ",";
                      if (best === semis) return ";";
                      if (best === tabs) return "\t";
                      return ",";
                    };
                    const parseCsvText = (input) => {
                      const delim = detectDelimiter(input);
                      const grid = [];
                      let row = [];
                      let cur = "";
                      let inQuotes = false;
                      for (let i = 0; i < input.length; i++) {
                        const ch = input[i];
                        const next = input[i + 1];
                        if (inQuotes) {
                          if (ch === "\"" && next === "\"") { cur += "\""; i++; continue; }
                          if (ch === "\"") { inQuotes = false; continue; }
                          cur += ch;
                          continue;
                        }
                        if (ch === "\"") { inQuotes = true; continue; }
                        if (ch === delim) { row.push(cur); cur = ""; continue; }
                        if (ch === "\n") {
                          row.push(cur); cur = "";
                          if (row.some(v => String(v || "").trim() !== "")) grid.push(row);
                          row = [];
                          continue;
                        }
                        if (ch === "\r") continue;
                        cur += ch;
                      }
                      row.push(cur);
                      if (row.some(v => String(v || "").trim() !== "")) grid.push(row);
                      return grid;
                    };

                    const grid = parseCsvText(text);
                    if (grid.length > 0) {
                      const header = grid[0].map(h => String(h || "").trim());
                      for (let i = 1; i < grid.length; i++) {
                        const cols = grid[i] || [];
                        if (cols.every(c => String(c || "").trim() === "")) continue;
                        const obj = {};
                        header.forEach((h, idx) => { obj[h] = String(cols[idx] ?? "").trim(); });
                        rows.push(obj);
                      }
                    }
                  }
                  const norm = rows.map((r, idx) => {
                    const normalizeDateInput = (v) => {
                      const s = String(v || "").trim();
                      if (!s) return "";
                      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
                      const m1 = s.match(/^(\d{4})[/.](\d{1,2})[/.](\d{1,2})$/);
                      if (m1) return `${m1[1]}-${String(m1[2]).padStart(2, "0")}-${String(m1[3]).padStart(2, "0")}`;
                      const m2 = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/);
                      if (m2) return `${m2[3]}-${String(m2[1]).padStart(2, "0")}-${String(m2[2]).padStart(2, "0")}`;
                      const m3 = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
                      if (m3) return `${m3[3]}-${String(m3[1]).padStart(2, "0")}-${String(m3[2]).padStart(2, "0")}`;
                      return s;
                    };
                    const campusRaw = r.campus || r.Campus || userCampus || "";
                    const campus = String(campusRaw).toLowerCase();
                    let id = r.id || r.ID || "";
                    const nameRaw = String(r.fullname || r.name || r.studentName || r["Student Name"] || r["Full Name"] || "").trim();
                    const parseName = (s) => {
                      const t = String(s || "").trim();
                      if (!t) return { first: "", middle: "", last: "", full: "" };
                      if (t.includes(",")) {
                        const [lastPart, restPart] = t.split(",", 2).map(x => String(x || "").trim());
                        const restTokens = restPart ? restPart.split(/\s+/).filter(Boolean) : [];
                        if (restTokens.length === 0) return { first: "", middle: "", last: lastPart, full: lastPart };
                        const first = restTokens[0] || "";
                        const middle = restTokens.slice(1).join(" ");
                        const full = `${first}${middle ? " " + middle : ""} ${lastPart}`.trim();
                        return { first, middle, last: lastPart, full };
                      }
                      const parts = t.split(/\s+/).filter(Boolean);
                      if (parts.length === 1) return { first: parts[0], middle: "", last: "", full: parts[0] };
                      if (parts.length === 2) return { first: parts[0], middle: "", last: parts[1], full: t };
                      return { first: parts[0], middle: parts.slice(1, -1).join(" "), last: parts[parts.length - 1], full: t };
                    };
                    const fromSingle = parseName(nameRaw);

                    const firstName = String(r.firstName || r.firstname || r.FirstName || r["First Name"] || fromSingle.first || "").trim();
                    const lastName = String(r.lastName || r.lastname || r.LastName || r["Last Name"] || fromSingle.last || "").trim();
                    const middle = String(r.middleName || r.middlename || r.MiddleName || r["Middle Name"] || fromSingle.middle || "").trim();
                    const fullname = String(nameRaw || `${firstName}${middle ? " " + middle : ""} ${lastName}`.trim()).trim();
                    const birthRaw = r.birthdate || r.Birthdate || r["Birth Date"] || r["Birthdate"] || r["Birthdate (mm/dd/yyyy)"] || "";
                    const birthdate = normalizeDateInput(birthRaw);
                    return {
                      id,
                      lrn: r.lrn || r.LRN || "",
                      firstName,
                      middleName: middle,
                      lastName,
                      fullname,
                      gradeLevel: r.gradeLevel || r["Grade Level"] || "",
                      levelName: r.levelName || r["Level Name"] || r["Tuition Level"] || "",
                      strand: r.strand || r.Strand || "",
                      campus,
                      gender: r.gender || r.Gender || "",
                      birthdate,
                      address: r.address || r.Address || "",
                      contact: r.contact || r.Contact || "",
                      paymentMode: r.paymentMode || r["Payment Mode"] || "",
                      enrollmentStatus: r.enrollmentStatus || r["Enrollment Status"] || "",
                      section: r.section || r.Section || "",
                      enrollmentYear: Number(r.enrollmentYear || r["Enrollment Year"] || new Date().getFullYear())
                    };
                  });
                  const existing = getAllStudents();
                  const required = (s) => Boolean((s.fullname || (s.firstName && s.lastName)) && s.gradeLevel && s.campus && s.birthdate);
                  const dupeOf = (s) => existing.find(x =>
                    (s.lrn && x.lrn && String(s.lrn).trim() === String(x.lrn).trim()) ||
                    (String(s.firstName||"").trim().toLowerCase() === String(x.firstName||"").trim().toLowerCase() &&
                     String(s.lastName||"").trim().toLowerCase() === String(x.lastName||"").trim().toLowerCase() &&
                     String(s.birthdate||"").trim() === String(x.birthdate||"").trim())
                  );
                  const rowsWithStatus = norm.map(s => {
                    const errors = [];
                    if (!required(s)) {
                      if (!s.gradeLevel) errors.push("gradeLevel");
                      if (!s.campus) errors.push("campus");
                      if (!s.birthdate) errors.push("birthdate");
                    }
                    let status = errors.length ? "Missing required" : "OK";
                    const dupe = dupeOf(s);
                    if (dupe) { status = "Update existing"; s.id = dupe.id; }
                    if (!isAdmin && userCampus && s.campus && s.campus !== userCampus) { errors.push("campus mismatch"); s.campus = userCampus; }
                    return { ...s, status, errors };
                  });
                  const counts = rowsWithStatus.reduce((a,r)=>{a.total++; if(r.status==="OK")a.ok++; if(r.status==="Update existing")a.update++; if(r.status==="Missing required")a.missing++; return a;},{total:0,ok:0,update:0,missing:0});
                  setImportPreview({ rows: rowsWithStatus, counts });
                } catch (err) {
                  console.warn(err);
                  alert("Failed to import students. Use CSV or JSON with correct columns.");
                } finally {
                  e.target.value = "";
                }
              };
              reader.readAsText(file);
            }}
          />
        </div>
      </div>

      <div className="card p-3 mb-3">
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label">Search</label>
            <input
              className="form-control"
              placeholder="Search by name or ID"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Grade Level</label>
            <select className="form-select" value={gradeFilter} onChange={e => setGradeFilter(e.target.value)}>
              <option value="">All</option>
              {grades.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Strand</label>
            <select className="form-select" value={strandFilter} onChange={e => setStrandFilter(e.target.value)}>
              <option value="">All</option>
              {strands.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Student Status</label>
            <select className="form-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              {statuses.map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Campus</label>
            <input className="form-control" value={isAdmin ? "All / Admin" : (userCampus || "")} disabled />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th style={{width: 110}}>ID</th>
                <th>Name</th>
                <th>Grade</th>
                <th>Section</th>
                <th>Status</th>
                <th>Campus</th>
                <th style={{width: 160}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(s => (
                <tr key={s.id}>
                  <td>{s.id}</td>
                  <td>{s.fullname || `${s.firstName || ""} ${s.lastName || ""}`}</td>
                  <td>{s.gradeLevel || ""}</td>
                  <td>{s.section || s.strand || ""}</td>
                  <td>{s.enrollmentStatus || ""}</td>
                  <td className="text-capitalize">{s.campus || ""}</td>
                  <td>
                    <Link to={`/sis/students/${s.id}/enrollment-form`} className="btn btn-sm btn-outline-primary me-2">Enrollment Form</Link>
                    <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => setEditId(s.id)}>Edit</button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => {
                        if (!window.confirm("Delete this student? This cannot be undone.")) return;
                        Promise.resolve(deleteStudent(s.id)).then(() => {
                          setStudents(getAllStudents());
                          setRev(r => r + 1);
                        }).catch(() => {
                          alert("Failed to delete student.");
                        });
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center text-muted">No students found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editId && (
        <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Student</h5>
                <button type="button" className="btn-close" onClick={() => setEditId(null)}></button>
              </div>
              <div className="modal-body">
                {editStudent && (
                  <StudentForm
                    initial={editStudent}
                    onSubmit={(rec) => {
                      Promise.resolve(updateStudent(editStudent.id, rec)).then(() => {
                        setRev(r => r + 1);
                        setStudents(getAllStudents());
                        setEditId(null);
                        alert("Student updated.");
                      }).catch(() => {
                        alert("Failed to update student.");
                      });
                    }}
                    onCancel={() => setEditId(null)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {importPreview && (
        <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.4)" }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import Preview</h5>
                <button type="button" className="btn-close" onClick={() => setImportPreview(null)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  Total: {importPreview.counts.total} • New: {importPreview.counts.ok} • Update: {importPreview.counts.update} • Missing required: {importPreview.counts.missing}
                  <div className="small mt-1">Required: gradeLevel, campus, birthdate. Non-admin imports are forced into your campus.</div>
                </div>
                <div className="table-responsive" style={{ maxHeight: 420 }}>
                  <table className="table table-sm table-hover">
                    <thead className="table-light">
                      <tr><th>#</th><th>Status</th><th>ID</th><th>Name</th><th>Grade</th><th>Campus</th><th>Birthdate</th><th>Section</th><th>Payment Mode</th></tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.map((r,i)=>(
                        <tr key={i} className={r.status==="Missing required"?"table-warning":(r.status==="Update existing"?"table-secondary":"")}>
                          <td>{i+1}</td><td>{r.status}{r.errors?.length?` (${r.errors.join(", ")})`:""}</td>
                          <td>{r.id}</td><td>{r.fullname || `${r.firstName||""} ${r.lastName||""}`}</td>
                          <td>{r.gradeLevel}</td><td className="text-capitalize">{r.campus}</td><td>{r.birthdate}</td>
                          <td>{r.section}</td><td>{r.paymentMode||""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setImportPreview(null)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={async () => {
                  const rows = importPreview.rows;
                  let created=0,updated=0,skipped=0;
                  for (const r of rows) {
                    if (r.status === "Missing required") { skipped++; continue; }
                    try {
                      const clean = { ...r };
                      delete clean.status;
                      delete clean.errors;
                      if (clean.section) clean.sections = [clean.section];
                      clean.gender = String(clean.gender||"").toLowerCase().startsWith("m") ? "male" : (String(clean.gender||"").toLowerCase().startsWith("f") ? "female" : "");
                      if (r.status === "Update existing") {
                        await updateStudent(String(clean.id), clean);
                        updated++;
                      } else {
                        if (!clean.id) clean.id = await generateAvailableStudentId(clean.campus);
                        await addStudent(clean);
                        created++;
                      }
                    } catch { skipped++; }
                  }
                  setImportPreview(null);
                  await refreshStudentsFromFirestore();
                  setStudents(getAllStudents());
                  setRev(r => r + 1);
                  alert(`Import complete. Created: ${created}, Updated: ${updated}, Skipped: ${skipped}`);
                }}>Import</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
